import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Building Financial Resilience',
    content: [
      '<strong>Learning Goal:</strong> Learn practical skills to improve financial security, build emotional resilience toward financial stress, and apply budgeting and saving tools effectively.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• Budgeting as empowerment, not restriction',
      '• Emergency fund principles and the 3-6 month rule',
      '• The role of financial literacy and planning',
      '• Resilience behaviors: saving small, tracking spending, mindful consumption',
      '• Managing financial setbacks and reframing failure',
      '<br/>',
      '<strong>Discussion:</strong> Financial resilience isn\'t about having unlimited resources–it\'s about having the knowledge, habits, and mindset to handle financial challenges with confidence.'
    ]
  },
  {
    title: 'Budgeting as Empowerment',
    content: [
      'A budget is not a punishment–it\'s a tool that gives you control over your money and reduces financial anxiety.',
      '<br/>',
      '<strong>Why Budgeting Works:</strong>',
      '• <strong>Creates awareness:</strong> You know where your money goes',
      '• <strong>Reduces stress:</strong> Eliminates surprise expenses',
      '• <strong>Enables choices:</strong> You decide how to allocate resources',
      '• <strong>Builds confidence:</strong> You feel in control of your finances',
      '<br/>',
      '<strong>Simple Budgeting Steps:</strong>',
      '1. <strong>Track income:</strong> Know what money comes in regularly',
      '2. <strong>List expenses:</strong> Categorize fixed and variable costs',
      '3. <strong>Set limits:</strong> Allocate money to different categories',
      '4. <strong>Monitor progress:</strong> Review and adjust monthly',
      '5. <strong>Celebrate wins:</strong> Acknowledge financial progress',
      '<br/>',
      '<strong>Budget Categories to Consider:</strong>',
      '• <strong>Essentials:</strong> Housing, food, transportation, utilities',
      '• <strong>Savings:</strong> Emergency fund, retirement, goals',
      '• <strong>Debt payment:</strong> Credit cards, loans, minimum payments',
      '• <strong>Discretionary:</strong> Entertainment, dining, hobbies',
      '<br/>',
      '<strong>Key Insight:</strong> Start with a simple 50/30/20 rule: 50% needs, 30% wants, 20% savings/debt.'
    ]
  },
  {
    title: 'Emergency Fund Essentials',
    content: [
      'An emergency fund is your financial safety net, providing peace of mind and preventing small problems from becoming big crises.',
      '<br/>',
      '<strong>Why Emergency Funds Matter:</strong>',
      '• <strong>Prevents debt:</strong> Avoid high-interest loans for emergencies',
      '• <strong>Reduces stress:</strong> Handle unexpected expenses calmly',
      '• <strong>Enables choices:</strong> Don\'t rush into poor decisions',
      '• <strong>Builds confidence:</strong> Feel prepared for life\'s uncertainties',
      '<br/>',
      '<strong>The 3-6 Month Rule:</strong>',
      '• <strong>3 months:</strong> Basic emergency coverage for most people',
      '• <strong>6 months:</strong> Recommended for those with variable income or dependents',
      '• <strong>Calculate:</strong> Multiply monthly expenses by 3 or 6',
      '<br/>',
      '<strong>Building Your Fund:</strong>',
      '• <strong>Start small:</strong> Save $25-50 per week initially',
      '• <strong>Automate:</strong> Set up automatic transfers',
      '• <strong>Cut back:</strong> Find one expense to reduce temporarily',
      '• <strong>Use windfalls:</strong> Tax refunds, bonuses, gifts',
      '• <strong>High-yield account:</strong> Earn more interest on savings',
      '<br/>',
      '<strong>Emergency Examples:</strong> Car repair, medical bill, job loss, home repair, unexpected travel'
    ]
  },
  {
    title: 'Financial Literacy & Planning',
    content: [
      'Financial literacy is the foundation of financial resilience–understanding basic concepts empowers better decision-making.',
      '<br/>',
      '<strong>Key Financial Concepts:</strong>',
      '• <strong>Compound interest:</strong> Money earning money over time',
      '• <strong>Opportunity cost:</strong> What you give up when making choices',
      '• <strong>Risk vs. reward:</strong> Higher returns usually mean higher risk',
      '• <strong>Inflation:</strong> How money loses purchasing power over time',
      '• <strong>Cash flow:</strong> The movement of money in and out',
      '<br/>',
      '<strong>Financial Planning Basics:</strong>',
      '• <strong>Short-term goals:</strong> 1-3 years (emergency fund, debt payoff)',
      '• <strong>Medium-term goals:</strong> 3-10 years (home purchase, education)',
      '• <strong>Long-term goals:</strong> 10+ years (retirement, legacy)',
      '<br/>',
      '<strong>Free Resources for Learning:</strong>',
      '• <strong>Books:</strong> "The Total Money Makeover" by Dave Ramsey',
      '• <strong>Online:</strong> Khan Academy, Investopedia, Consumer Financial Protection Bureau',
      '• <strong>Apps:</strong> Mint, YNAB (You Need A Budget), Acorns',
      '• <strong>Local:</strong> Credit unions, community education programs',
      '<br/>',
      '<strong>Takeaway:</strong> Financial literacy is a skill that improves with practice, not something you\'re born with.'
    ]
  },
  {
    title: 'Resilience Behaviors & Mindset',
    content: [
      'Financial resilience combines practical habits with a positive mindset that views setbacks as learning opportunities.',
      '<br/>',
      '<strong>Practical Resilience Behaviors:</strong>',
      '• <strong>Save small amounts regularly:</strong> Consistency beats large lump sums',
      '• <strong>Track spending daily:</strong> Awareness prevents overspending',
      '• <strong>Mindful consumption:</strong> Pause before purchases',
      '• <strong>Multiple income streams:</strong> Reduce dependence on one source',
      '• <strong>Regular financial check-ins:</strong> Monthly reviews and adjustments',
      '<br/>',
      '<strong>Emotional Resilience:</strong>',
      '• <strong>Reframe failure:</strong> Setbacks are feedback, not final',
      '• <strong>Celebrate progress:</strong> Acknowledge small wins',
      '• <strong>Practice patience:</strong> Financial change takes time',
      '• <strong>Seek support:</strong> Talk to trusted friends or professionals',
      '• <strong>Self-compassion:</strong> Be kind during the learning process',
      '<br/>',
      '<strong>Managing Setbacks:</strong>',
      '• <strong>Accept emotions:</strong> It\'s normal to feel disappointed',
      '• <strong>Analyze objectively:</strong> What went wrong? What can be learned?',
      '• <strong>Adjust plans:</strong> Modify approach based on new information',
      '• <strong>Keep going:</strong> One setback doesn\'t erase all progress',
      '<br/>',
      '<strong>Key Insight:</strong> Financial resilience is built through consistent small actions and a growth mindset.'
    ],
    additionalSection: {
      title: 'Financial Resilience Planning Workshop',
      content: [
        'Design your personal Financial Resilience Plan in small groups:',
        '<br/>',
        '<strong>Short-term Relief Actions (Next 30 days):</strong>',
        '• Track daily spending for one week',
        '• Create a simple budget using 50/30/20 rule',
        '• Set up automatic savings transfer ($25-50/week)',
        '• Review and cancel unused subscriptions',
        '<br/>',
        '<strong>Long-term Stability Actions (3-6 months):</strong>',
        '• Build emergency fund to 1 month of expenses',
        '• Pay off one high-interest debt',
        '• Learn one new financial concept monthly',
        '• Set up retirement savings if not already',
        '<br/>',
        '<strong>Group Sharing:</strong> After 15 minutes, share best practices and accountability partners.',
        '<br/>',
        '<strong>Reflection:</strong> Which action feels most achievable? Who can support your financial resilience journey?'
      ]
    }
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 4: Building Financial Resilience! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about financial resilience and planning.',
        'The quiz will help you review key concepts about building financial security.'
      ]
    }
  }
];

const FinancialModule4Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('financialM4');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/financial-module-4/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/financial-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left financial-panel"
        >
          <div className="module-header">
            <h1>Module 4</h1>
            <p className="module-subtitle">Building Financial Resilience</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Learn practical skills to improve financial security, build emotional resilience toward financial stress, and apply budgeting and saving tools effectively.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/financial-module-4/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialModule4Page;

